// ═══════════════════════════════════════════════════════════════════
//  modules/ah/AhAnnonce.js
//  AhAnnonce
// ═══════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from "react";
import { API_BASE } from "../../socle/api";
import { THEME } from "../../socle/theme";
import { actionInfo, argent, etatInfo, horodate, nomObjet, nombre } from "./ah";

// C'est cet écran qui répond à « j'ai mis deux stacks, il m'en manque un ».
// Il montre l'annonce, chaque vente, chaque paiement, et le compte qui dit
// si ça tombe juste — parce que la vraie réponse est un COMPTE, pas une liste.
export function AhAnnonce({ id, token, onFermer }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/ah/annonces/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setData(await res.json());
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [id, token]);
  useEffect(() => { load(); }, [load]);

  const retour = (
    <button onClick={onFermer} style={{ background: "transparent", border: `1px solid ${THEME.border}`, color: THEME.textMuted, borderRadius: 6, padding: "6px 12px", cursor: "pointer", fontSize: 13 }}>
      ← Retour au journal
    </button>
  );

  if (loading) return <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>{retour}<div style={{ color: THEME.textMuted }}>Chargement de l'annonce {id}...</div></div>;
  if (!data || data.error) return <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>{retour}<div style={{ color: THEME.red }}>{data?.error || "Erreur de chargement."}</div></div>;

  const a = data.annonce;
  const c = data.comptes;
  const e = etatInfo(a.etat);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        {retour}
        <h3 style={{ color: THEME.text, fontSize: 17, fontWeight: 800, margin: 0 }}>
          Annonce {a.id} · {nomObjet(a.identifiant_lisible)}
        </h3>
        <span style={{ background: e.fond, color: e.color, fontSize: 11, fontWeight: 700, padding: "3px 8px", borderRadius: 4 }}>{e.label}</span>
      </div>

      {/* ── LE COMPTE. En haut, en gros, avant tout le reste. ──────── */}
      {/* C'est la seule ligne qui tranche un litige : le reste sert à */}
      {/* comprendre POURQUOI, une fois qu'on sait s'il y a un problème. */}
      <div style={{
        background: c.equilibre ? THEME.emeraldDim : THEME.redDim,
        border: `1px solid ${c.equilibre ? THEME.emerald : THEME.red}`,
        borderRadius: 8, padding: "14px 18px",
      }}>
        <div style={{ color: c.equilibre ? THEME.emerald : THEME.red, fontSize: 14, fontWeight: 700, marginBottom: 6 }}>
          {c.equilibre ? "✅ Les comptes tombent juste" : "⛔ Les comptes ne tombent PAS juste"}
        </div>
        <div style={{ color: THEME.text, fontSize: 13, fontVariantNumeric: "tabular-nums" }}>
          {nombre(c.vendu)} vendu{c.vendu > 1 ? "s" : ""} + {nombre(c.reste)} restant{c.reste > 1 ? "s" : ""}
          {" = "}<strong>{nombre(c.vendu + c.reste)}</strong>
          {"  ·  "}départ : <strong>{nombre(c.quantite_initiale)}</strong>
          {!c.equilibre && (
            <span style={{ color: THEME.red, fontWeight: 700 }}>
              {"  ·  écart de "}{c.vendu + c.reste - c.quantite_initiale}
            </span>
          )}
        </div>
        {!c.equilibre && (
          <div style={{ color: THEME.textMuted, fontSize: 11, marginTop: 6, lineHeight: 1.6 }}>
            Un écart négatif : des unités ont disparu. Positif : des unités ont été livrées en trop.
            Dans les deux cas, la liste des ventes ci-dessous est la seule trace de ce qui s'est passé.
          </div>
        )}
      </div>

      {/* ── L'annonce ─────────────────────────────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10 }}>
        {[
          ["Vendeur", a.vendeur_pseudo],
          ["Catégorie", a.categorie],
          ["Prix unitaire", argent(a.prix_unitaire)],
          ["Quantité de départ", nombre(a.quantite_initiale)],
          ["Reste en vente", nombre(a.quantite_restante)],
          ["Serveur d'origine", a.serveur_origine],
          ["Posée le", horodate(a.posee_le)],
          ["Expire le", horodate(a.expire_le)],
          ...(a.close_le ? [["Close le", horodate(a.close_le)]] : []),
        ].map(([label, valeur]) => (
          <div key={label} style={{ background: THEME.surface, borderRadius: 6, padding: "10px 14px" }}>
            <div style={{ color: THEME.textMuted, fontSize: 10, fontWeight: 600, marginBottom: 4 }}>{label.toUpperCase()}</div>
            <div style={{ color: THEME.text, fontSize: 13 }}>{valeur}</div>
          </div>
        ))}
      </div>

      {/* ── L'argent ──────────────────────────────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10 }}>
        {[
          ["Encaissé des acheteurs", argent(c.encaisse), THEME.text],
          ["Dû au vendeur", argent(c.du_au_vendeur), THEME.emerald],
          ["Taxe prélevée", argent(c.taxe), c.taxe > 0 ? THEME.amber : THEME.textDim],
          ["Paiements en attente", nombre(c.paiements_en_attente), c.paiements_en_attente > 0 ? THEME.red : THEME.textDim],
        ].map(([label, valeur, couleur]) => (
          <div key={label} style={{ background: THEME.surface, borderRadius: 6, padding: "10px 14px" }}>
            <div style={{ color: THEME.textMuted, fontSize: 10, fontWeight: 600, marginBottom: 4 }}>{label.toUpperCase()}</div>
            <div style={{ color: couleur, fontSize: 15, fontWeight: 700 }}>{valeur}</div>
          </div>
        ))}
      </div>

      {/* ── Les ventes ────────────────────────────────────────────── */}
      <div>
        <div style={{ color: THEME.textMuted, fontSize: 11, fontWeight: 700, marginBottom: 10, textTransform: "uppercase" }}>
          Les ventes ({data.ventes.length})
        </div>
        {data.ventes.length === 0 ? (
          <div style={{ color: THEME.textDim, fontSize: 12 }}>Rien n'a encore été vendu sur cette annonce.</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, minWidth: 660 }}>
              <thead>
                <tr style={{ color: THEME.textMuted, textAlign: "left" }}>
                  {["Quand", "Acheteur", "Quantité", "Prix unit.", "Montant", "Vendeur payé", "Serveur"].map((h) => (
                    <th key={h} style={{ padding: "6px 10px", fontWeight: 600, fontSize: 10, textTransform: "uppercase", borderBottom: `1px solid ${THEME.border}`, whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.ventes.map((v) => (
                  <tr key={v.id} style={{ borderBottom: `1px solid ${THEME.border}` }}>
                    <td style={{ padding: "6px 10px", color: THEME.textMuted, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>{horodate(v.vendue_le)}</td>
                    <td style={{ padding: "6px 10px", color: THEME.text }}>{v.acheteur_pseudo}</td>
                    <td style={{ padding: "6px 10px", color: THEME.text, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{nombre(v.quantite)}</td>
                    <td style={{ padding: "6px 10px", color: THEME.textMuted, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{argent(v.prix_unitaire)}</td>
                    <td style={{ padding: "6px 10px", color: THEME.text, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{argent(v.montant)}</td>
                    <td style={{ padding: "6px 10px", whiteSpace: "nowrap" }}>
                      {/* Une vente sans ligne de paiement n'est PAS « payée » : */}
                      {/* c'est une vente dont personne ne sait si le vendeur a  */}
                      {/* été crédité. Le dire, plutôt que mettre un ✅.         */}
                      {!v.paiement ? (
                        <span style={{ color: THEME.amber }} title="Aucune ligne dans oa_paiement pour cette vente">⚠ aucun paiement écrit</span>
                      ) : v.paiement.verse ? (
                        <span style={{ color: THEME.emerald }}>✅ {argent(v.paiement.montant_net)}</span>
                      ) : (
                        <span style={{ color: THEME.red }}>⏳ {argent(v.paiement.montant_net)} en attente</span>
                      )}
                    </td>
                    <td style={{ padding: "6px 10px", color: THEME.textMuted }}>{v.serveur}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Le journal de cette annonce ───────────────────────────── */}
      <div>
        <div style={{ color: THEME.textMuted, fontSize: 11, fontWeight: 700, marginBottom: 10, textTransform: "uppercase" }}>
          Ce que le journal en dit ({data.journal.length})
        </div>
        {data.journal.length === 0 ? (
          <div style={{ color: THEME.textDim, fontSize: 12, lineHeight: 1.6 }}>
            Aucune ligne de journal ne cite cette annonce.
            {data.ventes.length > 0 && (
              <> C'est attendu pour les achats : le plugin écrit les lignes <code>ACHAT</code> sans
              <code> annonce_id</code>. Les ventes ci-dessus font foi.</>
            )}
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {data.journal.map((j) => {
              const ai = actionInfo(j.action);
              return (
                <div key={j.id} style={{ display: "flex", gap: 10, alignItems: "baseline", fontSize: 12, background: THEME.surface, borderRadius: 6, padding: "7px 12px", flexWrap: "wrap" }}>
                  <span style={{ color: THEME.textMuted, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>{horodate(j.horodatage)}</span>
                  <span style={{ color: ai.color, fontWeight: 600 }}>{ai.emoji} {ai.label}</span>
                  <span style={{ color: THEME.text }}>{j.acteur_pseudo || "—"}</span>
                  {j.montant !== null && <span style={{ color: THEME.emerald }}>{argent(j.montant)}</span>}
                  <span style={{ color: THEME.textDim, marginLeft: "auto" }}>{j.serveur}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
